import { useDispatch, useSelector } from 'react-redux';
import {
    setCertificates, setLoading, setSubmitting,
    addOrUpdateCertificate, removeCertificate,
    updateCertificateFormField,
    setUploadModalOpen,
    setEditModalOpen,
    setCertificateToEdit
} from '../../store/slices/hacienda/taxpayerCertificateSlice';

import TaxpayerCertificateService from '../../services/hacienda/TaxpayerCertificateService';
import { RootState } from '../../store/store';
import {
    TaxpayerCertificate,
    TaxpayerCertificateForm
} from '../../types/hacienda/TaxpayerCertificateTypes';

import useApiErrorHandler from '../useApiErrorHandler';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import useModals from '../useModals';

export const useTaxpayerCertificate = () => {
    const dispatch = useDispatch();
    const {
        form,
        error,
        certificates,
        isLoading,
        isSubmitting,
        editModalOpen,
        uploadModalOpen,
        certificateToEdit
    } = useSelector((state: RootState) => state.taxpayerCertificate);

    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();

    const fetchCertificates = async () => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();
            const response = await TaxpayerCertificateService.getAllFiles();
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setCertificates(data));
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    const uploadCertificate = async (file: File, fileType: 'CERTIFICATE' | 'PRIVATE_KEY') => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();

            const response = await TaxpayerCertificateService.uploadFile(file, fileType);
            const { type, title, message, statusCode, data } = response;

            if (statusCode === 201) {
                dispatch(addOrUpdateCertificate(data));
                openModal(type, { title, message, });
                closeUploadModal();
            } else {
                showAlert('error', 'Error', 'No se pudo subir el archivo.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };

    const updateCertificate = async (file: File, fileType: 'CERTIFICATE' | 'PRIVATE_KEY') => {
        dispatch(setSubmitting(true));
        showLoadingIndicator();
        try {
            const response = await TaxpayerCertificateService.updateFile(file, fileType);
            const { statusCode, data, type, title, message } = response;
            if (statusCode === 201) {
                dispatch(addOrUpdateCertificate(data));
                openModal(type, { title, message, });
                closeEditModal();
            } else {
                showAlert('error', 'Error', 'No se pudo actualizar el archivo.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };

    const deleteCertificate = async (fileType: 'CERTIFICATE' | 'PRIVATE_KEY') => {
        dispatch(setSubmitting(true));
        showLoadingIndicator();
        try {
            const response = await TaxpayerCertificateService.deleteFile(fileType);
            const { statusCode, type, title, message } = response;

            if (statusCode === 200) {
                dispatch(removeCertificate(fileType));
                openModal(type, { title, message, });
            }
        } catch (error: any) {
            handle(error);
        } finally {
            dispatch(setSubmitting(false));
            hideLoadingIndicator();
        }
    };

    const updateField = <K extends keyof TaxpayerCertificateForm>(
        field: K,
        value: TaxpayerCertificateForm[K]
    ) => {
        dispatch(updateCertificateFormField({ field, value }));
    };

    const selectCertificateToEdit = (certificate: TaxpayerCertificate) => {
        dispatch(setCertificateToEdit(certificate));
        openEditModal();
    };

    const clearCertificateToEdit = () => {
        dispatch(setCertificateToEdit(null));
    };

    const openUploadModal = () => dispatch(setUploadModalOpen(true));
    const closeUploadModal = () => dispatch(setUploadModalOpen(false));
    const openEditModal = () => dispatch(setEditModalOpen(true));
    const closeEditModal = () => dispatch(setEditModalOpen(false));

    return {
        form,
        error,
        certificates,
        isLoading,
        isSubmitting,
        editModalOpen,
        uploadModalOpen,
        certificateToEdit,

        fetchCertificates,
        uploadCertificate,
        updateCertificate,
        deleteCertificate,
        updateField,

        selectCertificateToEdit,
        clearCertificateToEdit,

        openUploadModal,
        closeUploadModal,
        openEditModal,
        closeEditModal
    };
};
